import { GetUserData, UpdateUserData, IUserRepository } from '../repository/interfaces';
import { auth } from '../../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { IAuthenticationUseCase, RegisterUserData } from './interfaces';

/**
 * Authentication & Account Management Use Case
 * Handles user registration, login, password recovery, and account management
 * User Stories: 1, 2
 */
export class AuthenticationUseCase implements IAuthenticationUseCase {
  constructor(private userRepo: IUserRepository) {
    // Removed BaseUseCase
  }

  /**
   * Register a new user with email and password
   * User Story 1: As a user, I can sign up with email/password or OAuth, verify email, and recover password
   */
  async registerUser(
    userData: RegisterUserData
  ): Promise<{
    user: GetUserData;
    firebaseUser: any;
  }> {
    try {
      const { password, confirmPassword, ...userInfo } = userData;

      // Validate passwords match
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Validate password strength
      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      // Check if user already exists
      const existingUser = await this.userRepo.getUserByEmail(userInfo.email);
      if (existingUser) {
        throw new Error('User with this email already exists');
      }

      if (userInfo.username) {
        const existingUsername = await this.userRepo.getUserByUsername(userInfo.username);
        if (existingUsername) {
          throw new Error('Username is already taken');
        }
      }

      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userInfo.email,
        password
      );

      // Create user in database
      const dbUser = await this.userRepo.createUser({
        ...userInfo,
        roles: ['USER'],
        authProvider: 'email'
      });

      // Send email verification
      // Note: In a real implementation, you'd send verification email here

      return {
        user: dbUser,
        firebaseUser: userCredential.user
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Sign in user with email and password
   */
  async loginUser(
    email: string,
    password: string
  ): Promise<{
    user: GetUserData;
    firebaseUser: any;
  }> {
    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get user from database
      const dbUser = await this.userRepo.getUserByEmail(email);
      if (!dbUser) {
        throw new Error('User not found in database');
      }

      // Update last login (repository applies updatedAt)
      await this.userRepo.updateUser(dbUser.id, {});

      return {
        user: dbUser,
        firebaseUser: userCredential.user
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Sign in with Google OAuth
   */
  async loginWithGoogle(): Promise<{
    user: GetUserData;
    firebaseUser: any;
    isNewUser: boolean;
  }> {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      const { user: firebaseUser } = result;
      const email = firebaseUser.email;
      if (!email) {
        throw new Error('Google account did not provide an email address');
      }
      const displayName = firebaseUser.displayName ?? '';
      const nameParts = displayName.trim().split(/\s+/).filter(Boolean);

      let dbUser = await this.userRepo.getUserByEmail(email);
      let isNewUser = false;

      if (!dbUser) {
        // Create new user in database
        dbUser = await this.userRepo.createUser({
          email,
          username: email.split('@')[0], // Use email prefix as username
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          roles: ['USER'],
          authProvider: 'google',
          googleId: firebaseUser.uid,
          emailVerified: firebaseUser.emailVerified
        });
        isNewUser = true;
      } else {
        // Update existing user with Google info
        await this.userRepo.updateUser(dbUser.id, {
          googleId: firebaseUser.uid,
          emailVerified: firebaseUser.emailVerified,
        });
      }

      return {
        user: dbUser,
        firebaseUser,
        isNewUser
      };
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  /**
   * Send password reset email
   * User Story 1: As a user, I can sign up with email/password or OAuth, verify email, and recover password
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      // Check if user exists
      const user = await this.userRepo.getUserByEmail(email);
      if (!user) {
        throw new Error('No user found with this email address');
      }

      // Send password reset email
      await sendPasswordResetEmail(auth, email);

      this.logInfo(`Password reset email sent to ${email}`);
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Change user password
   */
  async changePassword(
    userId: string,
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Reauthenticate user
      const credential = EmailAuthProvider.credential(user.email!, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      // Update password changed timestamp in database
      await this.userRepo.updateUser(userId, {});

      this.logInfo(`Password changed for user ${userId}`);
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Update user profile
   */
  async updateProfile(
    userId: string,
    updateData: Partial<UpdateUserData>
  ): Promise<GetUserData> {
    try {
      // Validate username uniqueness if being updated
      if (updateData.username) {
        const existingUser = await this.userRepo.getUserByUsername(updateData.username);
        if (existingUser && existingUser.id !== userId) {
          throw new Error('Username is already taken');
        }
      }

      const updatedUser = await this.userRepo.updateUser(userId, {
        ...updateData,
      });

      return updatedUser;
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Logout user
   */
  async logoutUser(): Promise<void> {
    try {
      await signOut(auth);
      this.logInfo('User logged out successfully');
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  /**
   * Verify user email
   */
  async verifyEmail(userId: string): Promise<void> {
    try {
      await this.userRepo.updateUser(userId, {
        emailVerified: true,
      });

      this.logInfo(`Email verified for user ${userId}`);
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Admin: Invite user with specific role
   * User Story 2: As Org Admin, I can invite users to specific roles
   */
  async inviteUser(
    adminUserId: string,
    inviteData: {
      email: string;
      username?: string;
      firstName?: string;
      lastName?: string;
      roles: string[];
      inviterMessage?: string;
    }
  ): Promise<{
    invitation: any;
    invitee: GetUserData;
  }> {
    try {
      // Check if admin has permission
      const isAdmin = await this.userRepo.isUserAdmin(adminUserId);
      if (!isAdmin) {
        throw new Error('Unauthorized: Only admins can invite users');
      }

      if (!inviteData.email) {
        throw new Error('Invite requires an email address');
      }

      // Check if user already exists
      const existingUser = await this.userRepo.getUserByEmail(inviteData.email);
      if (existingUser) {
        throw new Error('User with this email already exists');
      }

      // Create invited user (inactive until they accept)
      const invitedUser = await this.userRepo.createUser({
        email: inviteData.email,
        username: inviteData.username || inviteData.email.split('@')[0],
        firstName: inviteData.firstName || '',
        lastName: inviteData.lastName || '',
        roles: inviteData.roles,
        isActive: false,
        emailVerified: false
      });

      // In a real implementation, you'd create an invitation record
      // and send an invitation email with an acceptance link

      this.logInfo(`User ${invitedUser.email} invited by admin ${adminUserId}`);

      return {
        invitation: {
          id: `inv_${Date.now()}`,
          inviteeId: invitedUser.id,
          inviterId: adminUserId,
          invitedAt: new Date(),
          message: inviteData.inviterMessage
        },
        invitee: invitedUser
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Admin: Get all users with pagination
   * User Story 2: As Org Admin, I can invite users to specific roles
   */
  async getAllUsers(
    adminUserId: string,
    options: {
      limit?: number;
      offset?: number;
      role?: string;
      isActive?: boolean;
    } = {}
  ): Promise<{
    users: GetUserData[];
    total: number;
    hasMore: boolean;
  }> {
    console.log('[AuthenticationUseCase.getAllUsers] START - AdminUserId:', adminUserId, 'Options:', options);
    try {
      console.log('[AuthenticationUseCase.getAllUsers] Checking if user is admin');
      // Check if admin has permission
      const isAdmin = await this.userRepo.isUserAdmin(adminUserId);
      console.log('[AuthenticationUseCase.getAllUsers] Admin check result:', isAdmin);
      
      if (!isAdmin) {
        console.log('[AuthenticationUseCase.getAllUsers] ERROR: User is not admin');
        throw new Error('Unauthorized: Only admins can view all users');
      }

      console.log('[AuthenticationUseCase.getAllUsers] Calling userRepo.getAllUsers with limit:', options.limit);
      const users = await this.userRepo.getAllUsers(options.limit);
      console.log('[AuthenticationUseCase.getAllUsers] Retrieved users count:', users.length);
      
      const total = users.length; // In a real implementation, you'd have a separate count query

      const result = {
        users,
        total,
        hasMore: options.limit ? users.length === options.limit : false
      };
      
      console.log('[AuthenticationUseCase.getAllUsers] SUCCESS - Total:', total, 'HasMore:', result.hasMore);
      return result;
    } catch (error) { 
      console.error("[AuthenticationUseCase.getAllUsers] ERROR:", error); 
      throw error; 
    }
  }

  /**
   * Admin: Update user roles
   */
  async updateUserRoles(
    adminUserId: string,
    targetUserId: string,
    newRoles: string[]
  ): Promise<GetUserData> {
    try {
      // Check if admin has permission
      const isAdmin = await this.userRepo.isUserAdmin(adminUserId);
      if (!isAdmin) {
        throw new Error('Unauthorized: Only admins can update user roles');
      }

      // Prevent admin from removing their own admin role
      if (adminUserId === targetUserId && !newRoles.includes('admin')) {
        throw new Error('Cannot remove admin role from yourself');
      }

      const updatedUser = await this.userRepo.updateUser(targetUserId, {
        roles: newRoles,
      });

      this.logInfo(`User roles updated for ${targetUserId} by admin ${adminUserId}`);

      return updatedUser;
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Admin: Deactivate user account
   */
  async deactivateUser(
    adminUserId: string,
    targetUserId: string,
    reason?: string
  ): Promise<GetUserData> {
    try {
      // Check if admin has permission
      const isAdmin = await this.userRepo.isUserAdmin(adminUserId);
      if (!isAdmin) {
        throw new Error('Unauthorized: Only admins can deactivate users');
      }

      // Prevent admin from deactivating themselves
      if (adminUserId === targetUserId) {
        throw new Error('Cannot deactivate your own account');
      }

      const updatedUser = await this.userRepo.updateUser(targetUserId, {
        isActive: false,
      });

      this.logInfo(`User ${targetUserId} deactivated by admin ${adminUserId}`, { reason });

      return updatedUser;
    } catch (error) { console.error("Error:", error); throw error; }
  }
}
